/**
 * 出庫 · 逐項貨品編輯器（新增／改 `draft` 出庫單用；`ui-spec` 頁 8）
 *
 * ⚠️ **cbm 公式唔喺前端**：每箱 cbm 係後端（`@coeldery/sku-schema`）計好嘅值，
 *    呢度只係「箱數 × 每箱 cbm」嘅顯示估算（最終以 API 回嘅 `declaredCbm` 為準）。
 * ⚠️ 出庫一律**整箱**（`rules §七 C1`）→ 箱數只收 ≥1 整數。
 * ⚠️ 批次（`batchId`）**可以唔指定**；倉內該貨品有幾個批次時，由 `GET /api/inventory`
 *    （`location=warehouse`）讀返可用箱數 —— 唔准前端自己砌批次清單。
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormSelectField from '@coeldery/form-select-field';
import FormTextField from '@coeldery/form-text-field';
import { btnGhost, hint as hintStyle } from '@coeldery/ui-kit';
import { api, type BalanceRow, type Item } from '../lib/api';

export interface OutboundDraftLine {
  /** 前端臨時 id（key ＋ 刪除用） */
  key: string;
  itemId: string;
  /** `''` ＝ 唔指定批次（該貨品全部批次加埋） */
  batchId: string;
  cartons: string;
}

export const blankLine = (): OutboundDraftLine => ({
  key: `ol-${Math.random().toString(36).slice(2, 9)}`,
  itemId: '',
  batchId: '',
  cartons: '',
});

export interface OutboundLinesEditorProps {
  lines: OutboundDraftLine[];
  items: Item[];
  onChange: (lines: OutboundDraftLine[]) => void;
}

export default function OutboundLinesEditor({ lines, items, onChange }: OutboundLinesEditorProps) {
  const { t } = useTranslation();
  /** 貨品 → 倉內批次（`warehouse` 餘額；顯示可用箱數 ＋ 揀批次） */
  const [batches, setBatches] = useState<Record<string, BalanceRow[]>>({});

  const wanted = [...new Set(lines.map((l) => l.itemId).filter(Boolean))].filter((id) => !batches[id]);
  useEffect(() => {
    if (!wanted.length) return;
    let alive = true;
    void (async () => {
      const loaded: Record<string, BalanceRow[]> = {};
      for (const id of wanted) {
        try {
          const res = await api.inventory({ itemId: id, location: 'warehouse' });
          loaded[id] = (res.rows ?? []).filter((r) => (r.cartons ?? 0) > 0);
        } catch {
          loaded[id] = []; // 讀唔到就當「冇批次資料」：唔阻住開單（後端仍然會擋冇貨）
        }
      }
      if (alive) setBatches((b) => ({ ...b, ...loaded }));
    })();
    return () => {
      alive = false;
    };
  }, [wanted.join(','), batches]);

  const itemOptions = items.map((it) => ({ value: it.id, label: `${it.name}（${it.barcode}）` }));

  const patch = (key: string, field: keyof OutboundDraftLine, value: string | null) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, [field]: value ?? '' } : l)));

  /** 選貨品：手動揀過就要清返批次（唔可以留住上一件貨嘅批次） */
  const pickItem = (key: string, itemId: string) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, itemId, batchId: '' } : l)));

  /** 倉內可用箱數（有揀批次 → 該批次；冇 → 全部批次加埋） */
  const available = (l: OutboundDraftLine): number | null => {
    const rows = batches[l.itemId];
    if (!rows) return null;
    const picked = l.batchId ? rows.filter((r) => r.batchId === l.batchId) : rows;
    return picked.reduce((s, r) => s + (r.cartons ?? 0), 0);
  };

  const estimatedCbm = (l: OutboundDraftLine): string => {
    const it = items.find((i) => i.id === l.itemId);
    const cartons = Number(l.cartons);
    if (!it || !Number.isFinite(cartons) || cartons < 1) return '—';
    return String(Math.round(cartons * it.cbmPerCarton * 1e6) / 1e6);
  };

  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      {lines.map((l, i) => {
        const rows = batches[l.itemId] ?? [];
        const avail = available(l);
        const over = avail !== null && Number(l.cartons) > avail;
        return (
          <div
            key={l.key}
            style={{ border: '1px solid var(--color-divider)', borderRadius: '12px', padding: '12px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: '14px' }}>
                {t('out.item')} {i + 1} · {estimatedCbm(l)} cbm
              </strong>
              {lines.length > 1 && (
                <button
                  type="button"
                  style={{ ...btnGhost, minHeight: '34px' }}
                  onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
                >
                  {t('out.removeLine')}
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gap: '10px', marginTop: '8px' }}>
              <FormSelectField
                id={`f-ob-item-${l.key}`}
                labelKey="out.pickItem"
                value={l.itemId}
                onChange={(v) => pickItem(l.key, v)}
                options={[{ value: '', label: '—' }, ...itemOptions]}
              />
              {rows.length > 1 && (
                <FormSelectField
                  id={`f-ob-batch-${l.key}`}
                  labelKey="out.batch"
                  value={l.batchId}
                  onChange={(v) => patch(l.key, 'batchId', v)}
                  options={[
                    { value: '', label: t('out.batchAuto') },
                    ...rows.map((r) => ({
                      value: r.batchId,
                      label: `${r.batchNo ?? r.batchId}（${r.cartons} ${t('inv.cartons')}）`,
                    })),
                  ]}
                />
              )}
              <FormTextField
                id={`f-ob-cartons-${l.key}`}
                labelKey="out.cartons"
                type="number"
                value={l.cartons}
                onChange={(v) => patch(l.key, 'cartons', v)}
              />
              {avail !== null && (
                <p style={{ ...hintStyle, color: over ? 'var(--color-accent)' : undefined }}>
                  {t('out.warehouseAvailable', { n: avail })}
                </p>
              )}
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button type="button" style={btnGhost} onClick={() => onChange([...lines, blankLine()])}>
          {t('out.addLine')}
        </button>
      </div>
      <p style={hintStyle}>{t('out.hintCartons')}</p>
    </div>
  );
}
